/**
 * @voltxampere/web — Waveform pane visibility (Sprint 80.Y L-A-2).
 *
 * Closes Şefim's #2 observation ("osiloskop gizledim, açacak yer yok").
 * The existing `waveformExpanded` signal (vxa-waveform.ts:111) toggles
 * between 260 px expanded and 24 px collapsed. Şefim's tour hit the
 * collapsed-state UX failure: the 24 px strip clips the in-pane
 * toolbar's expand button, leaving the user unable to find a way back.
 *
 * Sprint 80.Y L-A-2 ships a separate signal for FULL pane visibility
 * controlled from outside the waveform pane itself (topbar button +
 * keyboard shortcut Cmd+Shift+O + palette command view.toggle-waveform-pane).
 * When hidden, the layout grid row collapses entirely. State persists
 * via localStorage so the user's preference survives reload.
 *
 * The existing `waveformExpanded` continues to govern the in-pane
 * collapse (260 ↔ 24 px) and is independent of this signal. Three
 * effective states:
 *
 *   1. visible + expanded   → 260 px full pane
 *   2. visible + collapsed  → 24 px strip with in-pane toolbar
 *   3. hidden               → 0 px (no pane in layout)
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal } from "@preact/signals-core";

const STORAGE_KEY = "vxa.waveformPaneVisible";

function loadInitial(): boolean {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === null) return true;
    return stored !== "false";
  } catch {
    return true;
  }
}

export const waveformPaneVisible = signal<boolean>(loadInitial());

export function setWaveformPaneVisible(visible: boolean): void {
  if (waveformPaneVisible.value === visible) return;
  waveformPaneVisible.value = visible;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(visible));
  } catch {
    // localStorage failures (private mode, quota) — fail soft; the
    // signal still flips for the current session.
  }
}

export function toggleWaveformPane(): void {
  setWaveformPaneVisible(!waveformPaneVisible.value);
}
